import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { fetchToken } from "./Auth";
import "../css/estatisticasProjeto.css";

function EstatisticasProjeto() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [projeto, setProjeto] = useState(null);
  const [valorGasto, setValorGasto] = useState(0);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const token = fetchToken();
        if (!token) throw new Error("Token não encontrado.");

        const resProjeto = await axios.get(
          `/api/projetocompleto/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (
          resProjeto.data.Status === "Success" &&
          resProjeto.data.Result.length > 0
        ) {
          setProjeto(resProjeto.data.Result[0]);
        } else {
          throw new Error("Projeto não encontrado ou acesso negado.");
        }

        try {
          const resCronograma = await axios.get(
            `/api/cronograma/${id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const itensDoCronograma = resCronograma.data || [];
          const somaTotalGasto = itensDoCronograma.reduce((accItens, item) => {
            const parcelas = item.mensal || [];
            return (
              accItens +
              parcelas.reduce(
                (acc, parcela) => acc + parseFloat(parcela.valor_simples || 0),
                0,
              )
            );
          }, 0);

          setValorGasto(somaTotalGasto);
        } catch (cronogramaErr) {
          console.error("Erro cronograma:", cronogramaErr.message);
          setValorGasto(0);
        }
      } catch (error) {
        console.error("Erro projeto:", error);
        alert("Erro ao carregar projeto. Verifique sua permissão.");
      } finally {
        setLoading(false);
      }
    };
    fetchDados();
  }, [id]);

  const calcularProgressoEtapa = (inicio, fim, situacao) => {
    if (!inicio || !fim) return 0;
    const hoje = new Date();
    const dataInicio = new Date(inicio);
    const dataFim = new Date(fim);

    if ((situacao || "").toLowerCase() === "concluído") return 100;
    if (hoje < dataInicio) return 0;
    if (hoje > dataFim) return 100;

    const totalDias = (dataFim - dataInicio) / (1000 * 3600 * 24);
    if (totalDias <= 0) return 100;
    const diasPassados = (hoje - dataInicio) / (1000 * 3600 * 24);
    return Math.round((diasPassados / totalDias) * 100);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-2" role="status"></div>
          <p className="text-muted">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  if (!projeto) {
    return (
      <div className="container mt-5 text-center">
        <h3>Projeto não encontrado.</h3>
        <Link to="/projetocompleto" className="btn btn-primary mt-3">
          Voltar
        </Link>
      </div>
    );
  }

  // --- LÓGICA FINANCEIRA EXATA (Com pesos iguais) ---

  // 1. Orçamento Original
  const valorRepasseOriginal = parseFloat(projeto.valor_repasse) || 0;
  const fonteRepasseOriginal = projeto.fonte_repasse;
  const valorContrapartidaOriginal =
    parseFloat(projeto.valor_contrapartida) || 0;
  const fonteContrapartidaOriginal = projeto.fonte_contrapartida;
  const valorOrcadoOriginalTotal =
    valorRepasseOriginal + valorContrapartidaOriginal;

  // 2. Licitação
  const valorLicitadoGlobal = parseFloat(projeto.valor_licitado) || 0;
  const valorLicitadoRepasse = parseFloat(projeto.valor_licitado_repasse) || 0;
  const fonteLicitadoRepasse = projeto.fonte_licitado_repasse;
  const valorLicitadoContrapartida =
    parseFloat(projeto.valor_licitado_contrapartida) || 0;
  const fonteLicitadoContrapartida = projeto.fonte_licitado_contrapartida;

  // 3. Cálculos de Consumo (Se já licitou usa o licitado, senão usa o orçado)
  const valorBaseConsumo =
    valorLicitadoGlobal > 0 ? valorLicitadoGlobal : valorOrcadoOriginalTotal;
  const valorRestante = Math.max(0, valorBaseConsumo - valorGasto);
  const percentualGasto =
    valorBaseConsumo > 0
      ? Math.round((valorGasto / valorBaseConsumo) * 100)
      : 0;

  // --- CRONOGRAMA ---
  const etapas = [1, 2, 3, 4, 5, 6].map((n) => {
    const inicio = projeto[`data_inicio_etapa${n}`];
    const fim = projeto[`data_termino_etapa${n}`];
    const situacao = projeto[`situacao_etapa${n}`];

    return {
      id: n,
      nome: projeto[`etapa${n}`] || `Etapa ${n}`,
      inicio: inicio ? new Date(inicio).toLocaleDateString("pt-BR") : "-",
      fim: fim ? new Date(fim).toLocaleDateString("pt-BR") : "-",
      situacao: situacao || "Não iniciado",
      progresso: calcularProgressoEtapa(inicio, fim, situacao),
      rawInicio: inicio,
      rawFim: fim,
    };
  });

  let etapaAtual = etapas.find(
    (e) => (e.situacao || "").toLowerCase() === "em andamento",
  );
  if (!etapaAtual)
    etapaAtual = etapas.find(
      (e) => (e.situacao || "").toLowerCase() === "não iniciado",
    );
  if (!etapaAtual) etapaAtual = etapas[etapas.length - 1];

  const progressoEtapaAtual = etapaAtual.progresso;
  const hoje = new Date();
  const dataFimEtapaAtual = etapaAtual.rawFim
    ? new Date(etapaAtual.rawFim)
    : null;
  const isAtrasada =
    (etapaAtual.situacao || "").toLowerCase() === "em andamento" &&
    dataFimEtapaAtual &&
    hoje > dataFimEtapaAtual;

  const getBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("concluído")) return "concluido";
    if (s.includes("andamento")) return "andamento";
    if (s.includes("atrasado")) return "atrasado";
    return "nao-iniciado";
  };

  return (
    <div className="stats-container">
      <div className="header-stats">
        <div>
          <h1 className="header-title">Painel Estatístico</h1>
          <span style={{ opacity: 0.8, fontSize: "0.9rem" }}>
            Projeto: {projeto.nomeprojeto}
          </span>
        </div>
        <Link to={`/viewprojetocompleto/${id}`} className="btn-back-custom">
          <i className="fas fa-arrow-left"></i> Voltar ao Projeto
        </Link>
      </div>

      <div className="stats-content">
        <div className="row g-4">
          {/* --- CARD FINANCEIRO (AGORA COM OS DOIS DESTAQUES GIGANTES) --- */}
          <div className="col-lg-4 col-md-12">
            <div className="stat-card d-flex flex-column h-100">
              <div className="stat-card-title border-bottom pb-2 mb-3">
                <i className="fas fa-wallet me-2"></i> Execução Orçamentária
              </div>

              {/* BLOCO 1: ORÇADO (INICIAL) */}
              <div className="bg-white border border-secondary rounded p-3 mb-3 shadow-sm">
                <div className="text-center mb-3">
                  <span className="stat-label-small d-block text-secondary text-uppercase mb-1 fw-bold">
                    Orçamento Inicial (Estimado)
                  </span>
                  <span className="fs-3 fw-bold text-dark">
                    R${" "}
                    {valorOrcadoOriginalTotal.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="row text-start border-top pt-2">
                  <div className="col-6 border-end">
                    <div
                      className="fw-bold text-dark"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Repasse:
                    </div>
                    <div
                      className="text-secondary fw-bold"
                      style={{ fontSize: "0.9rem" }}
                    >
                      R${" "}
                      {valorRepasseOriginal.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      className="text-muted text-truncate mt-1"
                      style={{ fontSize: "0.65rem", maxWidth: "100%" }}
                      title={fonteRepasseOriginal || "Não informada"}
                    >
                      {fonteRepasseOriginal || "Não informada"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div
                      className="fw-bold text-dark"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Contrapartida:
                    </div>
                    <div
                      className="text-secondary fw-bold"
                      style={{ fontSize: "0.9rem" }}
                    >
                      R${" "}
                      {valorContrapartidaOriginal.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      className="text-muted text-truncate mt-1"
                      style={{ fontSize: "0.65rem", maxWidth: "100%" }}
                      title={fonteContrapartidaOriginal || "Não informada"}
                    >
                      {fonteContrapartidaOriginal || "Não informada"}
                    </div>
                  </div>
                </div>
              </div>

              {/* BLOCO 2: LICITADO (FINAL) */}
              <div className="bg-light border border-primary rounded p-3 mb-4 shadow-sm">
                <div className="text-center mb-3">
                  <span className="stat-label-small d-block text-primary text-uppercase mb-1 fw-bold">
                    Orçamento Licitado (Contratado)
                  </span>
                  <span className="fs-3 fw-bold text-primary">
                    R${" "}
                    {valorLicitadoGlobal.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div
                  className="row text-start border-top border-primary pt-2"
                  style={{ borderColor: "rgba(13, 110, 253, 0.2) !important" }}
                >
                  <div
                    className="col-6 border-end border-primary"
                    style={{
                      borderColor: "rgba(13, 110, 253, 0.2) !important",
                    }}
                  >
                    <div
                      className="fw-bold text-dark"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Repasse:
                    </div>
                    <div
                      className="text-primary fw-bold"
                      style={{ fontSize: "0.9rem" }}
                    >
                      R${" "}
                      {valorLicitadoRepasse.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      className="text-muted text-truncate mt-1"
                      style={{ fontSize: "0.65rem", maxWidth: "100%" }}
                      title={fonteLicitadoRepasse || "Não informada"}
                    >
                      {fonteLicitadoRepasse || "Não informada"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div
                      className="fw-bold text-dark"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Contrapartida:
                    </div>
                    <div
                      className="text-primary fw-bold"
                      style={{ fontSize: "0.9rem" }}
                    >
                      R${" "}
                      {valorLicitadoContrapartida.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      className="text-muted text-truncate mt-1"
                      style={{ fontSize: "0.65rem", maxWidth: "100%" }}
                      title={fonteLicitadoContrapartida || "Não informada"}
                    >
                      {fonteLicitadoContrapartida || "Não informada"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Executado e Consumo */}
              <div className="mt-auto border-top pt-3">
                <div className="row mb-3">
                  <div className="col-6">
                    <span className="stat-label-small fw-bold">
                      Executado / Gasto
                    </span>
                    <div className="fs-6 text-dark fw-bold">
                      R${" "}
                      {valorGasto.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  <div className="col-6 text-end">
                    <span className="stat-label-small fw-bold">
                      Saldo Disponível
                    </span>
                    <div className="fs-6 text-success fw-bold">
                      R${" "}
                      {valorRestante.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between mb-1">
                  <span className="small text-muted">
                    Consumo do Orçamento Vigente
                  </span>
                  <span className="small fw-bold">{percentualGasto}%</span>
                </div>
                <div className="progress-container-custom">
                  <div
                    className={`progress-bar-custom ${percentualGasto > 90 ? "bg-danger" : "bg-success"}`}
                    style={{ width: `${percentualGasto}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div
              className="stat-card h-100"
              style={{
                borderTopColor: isAtrasada
                  ? "var(--danger)"
                  : "var(--blue-primary)",
              }}
            >
              <div className="stat-card-title">
                <i className="fas fa-clock me-2"></i> Etapa Vigente
              </div>

              <h4 className="fw-bold mb-3 text-dark">{etapaAtual.nome}</h4>

              <div className="d-flex justify-content-between mb-4 p-3 bg-light rounded">
                <div className="text-center">
                  <small
                    className="text-muted d-block text-uppercase"
                    style={{ fontSize: "0.7rem" }}
                  >
                    Início
                  </small>
                  <strong>{etapaAtual.inicio}</strong>
                </div>
                <div className="align-self-center text-muted">
                  <i className="fas fa-arrow-right"></i>
                </div>
                <div className="text-center">
                  <small
                    className="text-muted d-block text-uppercase"
                    style={{ fontSize: "0.7rem" }}
                  >
                    Término Previsto
                  </small>
                  <strong>{etapaAtual.fim}</strong>
                </div>
              </div>

              <div className="mt-auto">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span
                    className={`status-badge ${getBadgeClass(isAtrasada ? "atrasado" : etapaAtual.situacao)}`}
                  >
                    {isAtrasada ? "ATRASADO" : etapaAtual.situacao}
                  </span>
                  <span className="fw-bold">
                    {progressoEtapaAtual}% do tempo
                  </span>
                </div>

                <div className="progress-container-custom">
                  <div
                    className={`progress-bar-custom ${isAtrasada ? "bg-danger" : "bg-primary"}`}
                    style={{ width: `${progressoEtapaAtual}%` }}
                  ></div>
                </div>
                {isAtrasada && (
                  <small className="text-danger mt-1 d-block">
                    <i className="fas fa-exclamation-triangle"></i> Atenção: O
                    prazo desta etapa expirou.
                  </small>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="stat-card h-100">
              <div className="stat-card-title">
                <i className="fas fa-list-ul me-2"></i> Cronograma Geral
              </div>

              <div
                style={{
                  overflowY: "auto",
                  maxHeight: "400px",
                  margin: "-1rem",
                }}
              >
                <ul className="timeline-list">
                  {etapas.map((etapa) => {
                    const isActive = etapa.id === etapaAtual.id;
                    return (
                      <li
                        key={etapa.id}
                        className={`timeline-item ${isActive ? "active-stage" : ""}`}
                      >
                        <div className="timeline-info">
                          <strong>{etapa.nome}</strong>
                          <div className="timeline-dates">
                            <i className="far fa-calendar-alt me-1"></i>{" "}
                            {etapa.inicio} - {etapa.fim}
                          </div>
                        </div>
                        <div className="text-end">
                          <span
                            className={`status-badge ${getBadgeClass(etapa.situacao)}`}
                          >
                            {etapa.situacao || "---"}
                          </span>
                          {isActive && (
                            <div className="small text-primary mt-1 fw-bold">
                              Atual
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EstatisticasProjeto;
